/**
 * operatorSession — "the keys live with the person tapped in" (Jose 2026-09-30, Phase B security model).
 *
 * The shared iPad's MAIN Firebase session (shop account) must hold NO power to read pay/financials — so
 * that even the browser console can't pull other barbers' pay. Instead, a SEPARATE, EPHEMERAL Firebase
 * app authenticates as the CURRENT tapped-in operator (via the barberSignIn custom token), and all
 * SENSITIVE reads (barberPrivate pay, freshaHistory/walkInLog reports) go through THIS session. Firestore
 * rules then scope by the operator: owner/manager → all, barber → only their own, nobody → nothing.
 *
 * SECURITY: in-memory persistence ONLY — the operator token never touches IndexedDB/localStorage and is
 * gone on reload, switch, or idle. The main app keeps doing all non-sensitive POS work unchanged.
 * change-watch: functions/src/barberAuth.ts (barberSignIn wantToken), firestore.rules (barberPrivate).
 */
import { initializeApp, deleteApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signOut, inMemoryPersistence, setPersistence, Auth } from 'firebase/auth';
import { getFirestore, Firestore, initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { isManagerOrOwner } from '../shared/authority';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const APP_NAME = 'operator-session';

let opApp: FirebaseApp | null = null;
let opAuth: Auth | null = null;
let opDb: Firestore | null = null;
let currentBarberId: string | null = null;
let currentRole: string | null = null;
const listeners: Array<() => void> = [];

function ensureApp() {
  if (opApp) return;
  // Reuse if a prior instance lingers (HMR / re-import).
  const existing = getApps().find((a) => a.name === APP_NAME);
  opApp = existing || initializeApp(firebaseConfig, APP_NAME);
  opAuth = getAuth(opApp);
  // Ephemeral: the operator token must NEVER persist to IndexedDB (that would re-open the door).
  setPersistence(opAuth, inMemoryPersistence).catch(() => {});
  opDb = initializeFirestore(opApp, {
    ignoreUndefinedProperties: true,
    // Match the main db: auto-detect transport so operator reads work on both Safari/iOS (WebChannel
    // wedge → long-poll) and long-poll-hostile networks (→ WebChannel). (2026-10-07)
    experimentalAutoDetectLongPolling: true,
    localCache: memoryLocalCache(),
  });
}

/** Sign the tapped-in operator into the ephemeral session. `token` from barberSignIn({wantToken:true}). */
export async function startOperatorSession(token: string, barberId: string, role?: string): Promise<boolean> {
  if (!token) return false;
  try {
    ensureApp();
    await signInWithCustomToken(opAuth!, token);
    currentBarberId = barberId;
    currentRole = (role || '').toLowerCase() || null;
    notify();
    return true;
  } catch (e) {
    console.error('[operatorSession] sign-in failed:', e);
    currentBarberId = null;
    currentRole = null;
    return false;
  }
}

/** Tear down on operator switch / idle-lock / sign-out. */
export async function endOperatorSession(): Promise<void> {
  currentBarberId = null;
  currentRole = null;
  try { if (opAuth) await signOut(opAuth); } catch { /* best-effort */ }
  notify();
}

/** The operator's Firestore instance for SCOPED pay/financial reads. null when nobody is tapped in. */
export function getOperatorDb(): Firestore | null {
  return currentBarberId ? opDb : null;
}

export function getOperatorBarberId(): string | null { return currentBarberId; }
export function getOperatorRole(): string | null { return currentRole; }
/** True when the tapped-in operator can see ALL pay/financials (owner/manager). */
export function operatorSeesAll(): boolean {
  return isManagerOrOwner(currentRole);
}

/** Subscribe to operator-session changes (start/end) — used to re-run scoped subscriptions. */
export function onOperatorChange(cb: () => void): () => void {
  listeners.push(cb);
  return () => { const i = listeners.indexOf(cb); if (i >= 0) listeners.splice(i, 1); };
}
function notify() { listeners.forEach((l) => { try { l(); } catch { /* ignore */ } }); }

/** Full teardown (rare — e.g. shop sign-out). */
export async function destroyOperatorSession(): Promise<void> {
  await endOperatorSession();
  try { if (opApp) await deleteApp(opApp); } catch { /* ignore */ }
  opApp = null; opAuth = null; opDb = null;
}
